import React, { useState } from 'react';
import { InvoiceItem } from '../types';
import {
  StyledCard, CardTitleCompacto, MaterialItemContainer, InlineDeleteButton,
  DropdownList, DropdownItem,
} from '../cardStyles';
import {
  IpiCell, OrigemIpiChip, ItensHeader, ItensHeaderActions, CenarioPill, AddItemButton, PersonalizadoChip, SemCenarioAviso,
  ItensScroll, ItensGrade, DeleteCell,
} from '../style';
import type { CenarioFiscalDosItens } from '../useCenarioFiscalDosItens';
import trash from '../../../../../assets/production-flow/trash.svg';

// Rótulo curto da origem do IPI (D4) — mostrado como chip sob a alíquota.
const ORIGEM_IPI_LABEL: Record<string, string> = {
  NATUREZA_NAO_INCIDE: 'não incide',
  PERFIL_NCM_EMISSOR: 'NCM · emissor',
  PERFIL_NCM_TENANT: 'NCM',
  SEM_REGRA: 'sem regra',
};

interface CfopOption {
  id: number;
  code: string;
  description: string;
}

interface NcmOption {
  id: number;
  ncm: string;
}

interface Props {
  items: InvoiceItem[];
  setItems: React.Dispatch<React.SetStateAction<InvoiceItem[]>>;
  cfops: CfopOption[];
  cfopsPerItem: Map<number, CfopOption[]>;
  destStateId: number | null;
  isEmitida: boolean;
  onRemove: (item: { id: number; descricao: string; quantidade: number }) => void;
  fetchNcmsForItem: (item: InvoiceItem) => Promise<void>;
  ncmOptions: NcmOption[];
  onAddItem?: () => void;
  semEmissor?: boolean;
  cenario?: CenarioFiscalDosItens;
}

// 6px de padding lateral em vez de 8: são 12 células numéricas por linha, e os 48px que isso
// devolve entram no piso das colunas — numa tabela de 14 colunas o padding é caro.
const smallInputStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  borderRadius: '100px',
  padding: '4px 6px',
  fontSize: '12px',
  width: '100%',
  textAlign: 'right',
  background: '#fff',
  outline: 'none',
};

const pillInputStyle: React.CSSProperties = {
  ...smallInputStyle,
  background: '#ebebeb',
  borderRadius: '100px',
  textAlign: 'center',
  border: 'none',
};

// Linhas do mesmo orderItemId sao variantes do mesmo produto (grade) e compartilham
// NCM/CFOP. orderItemId nulo (SET NULL por edicao do pedido) nao casa com nada alem
// dela mesma - so a linha clicada muda.
const mesmoProduto = (p: InvoiceItem, item: InvoiceItem): boolean =>
  p.id === item.id || (p.orderItemId != null && p.orderItemId === item.orderItemId);

// Piso por coluna medido no pior conteúdo real de cada uma (NCM de 8 dígitos, "1.234.567,89",
// quantidade com 4 decimais) + padding e borda do input: `fr` puro encolhia a célula abaixo do
// texto e o input corta em silêncio, sem quebrar linha. Compartilhado pelo header e pelas linhas.
const GRID_COLS = [
  'minmax(120px, 2.5fr)', // Código / Descrição — absorve a folga
  'minmax(75px, 1fr)', // NCM/SH
  'minmax(49px, 0.6fr)', // O/CST
  'minmax(47px, 0.7fr)', // CFOP
  'minmax(36px, 0.5fr)', // UN
  'minmax(66px, 0.6fr)', // Qtd
  'minmax(74px, 1fr)', // Vl. Unit.
  'minmax(91px, 1.1fr)', // Vl. Total
  'minmax(81px, 1fr)', // B.Cálc ICMS
  'minmax(75px, 1fr)', // Vl. ICMS
  'minmax(67px, 1fr)', // Vl. IPI
  'minmax(49px, 0.7fr)', // Alíq. ICMS%
  'minmax(44px, 0.7fr)', // Alíq. IPI%
  'auto', // excluir
].join(' ');
// 13 colunas de intervalo: cada 2px economizados aqui valem 26px de piso.
const GRID_GAP = '6px';
const HEADERS = [
  'Código / Descrição', 'NCM/SH', 'O/CST', 'CFOP', 'UN',
  'Qtd', 'Vl. Unit.', 'Vl. Total', 'B.Cálc ICMS', 'Vl. ICMS',
  'Vl. IPI', 'Alíq. ICMS%', 'Alíq. IPI%',
];

const ItensSection: React.FC<Props> = ({
  items,
  setItems,
  cfops,
  cfopsPerItem,
  destStateId,
  isEmitida,
  onRemove,
  fetchNcmsForItem,
  ncmOptions,
  onAddItem,
  semEmissor = false,
  cenario,
}) => {
  const [activeCfopDropdown, setActiveCfopDropdown] = useState<number | null>(null);
  const [cfopSearch, setCfopSearch] = useState('');
  const [activeNcmDropdown, setActiveNcmDropdown] = useState<number | null>(null);
  const [ncmSearch, setNcmSearch] = useState('');

  // Com NCM definido e busca respondida, só entram os CFOPs com cenário para NCM+UF+emissora.
  const validCfopsForActive =
    activeCfopDropdown !== null ? cfopsPerItem.get(activeCfopDropdown) : undefined;
  const activeItem =
    activeCfopDropdown !== null ? items.find((i) => i.id === activeCfopDropdown) : undefined;
  const podeValidarCfop = !!activeItem?.ncmId && validCfopsForActive !== undefined;

  const activeCfopSource: CfopOption[] = podeValidarCfop ? validCfopsForActive ?? [] : cfops;
  const rotuloCfop = (cfop: CfopOption) =>
    cenario?.nomePorCfop.get(String(cfop.code)) ?? `${cfop.code} — ${cfop.description}`;

  const filteredCfops = activeCfopSource.filter((opt) => {
    const q = cfopSearch.toLowerCase();
    return (
      String(opt.code || '').toLowerCase().includes(q) ||
      String(opt.description || '').toLowerCase().includes(q) ||
      rotuloCfop(opt).toLowerCase().includes(q)
    );
  });

  const cenarioTopo = cenario?.naturezas.find((n) => n.taxConfigId === cenario.cenarioTopoId);
  const nomeCenarioTopo = cenarioTopo ? cenarioTopo.nome ?? cenarioTopo.cfop ?? undefined : undefined;

  const filteredNcms = ncmOptions.filter((opt) =>
    String(opt.ncm || '').toLowerCase().includes(ncmSearch.toLowerCase())
  );

  return (
    <StyledCard>
      <ItensHeader>
        <CardTitleCompacto style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>
          Itens da Nota
        </CardTitleCompacto>
        <ItensHeaderActions>
        {!isEmitida && cenario && cenario.naturezas.length > 0 && (
          <CenarioPill title={nomeCenarioTopo}>
            <label htmlFor="cenario-fiscal-itens">Cenário fiscal</label>
            <select
              id="cenario-fiscal-itens"
              value={cenario.cenarioTopoId ?? ''}
              onChange={(e) => cenario.mudarCenarioTopo(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Selecione…</option>
              {cenario.naturezas.map((n) => {
                const semCenario = cenario.itensSemCenario(n);
                return (
                  <option key={n.taxConfigId} value={n.taxConfigId}>
                    {n.nome ?? n.cfop}
                    {semCenario > 0 ? ` · ${semCenario} ${semCenario === 1 ? 'item' : 'itens'} sem cenário` : ''}
                  </option>
                );
              })}
            </select>
          </CenarioPill>
        )}
        {!isEmitida && onAddItem && (
          <AddItemButton type="button" onClick={onAddItem}>
            + Adicionar item
          </AddItemButton>
        )}
        </ItensHeaderActions>
      </ItensHeader>

      {!isEmitida && semEmissor && (
        <div style={{
          fontSize: '12px', color: '#0090D3', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '15px', height: '15px', borderRadius: '50%',
            border: '1px solid #0090D3', fontSize: '10px', fontStyle: 'italic', fontWeight: 700,
          }}>i</span>
          Selecione a empresa emissora (em Identificação) para habilitar CFOP e NCM.
        </div>
      )}

      <ItensScroll>
      <ItensGrade>
      <div style={{
        display: 'grid',
        gridTemplateColumns: GRID_COLS,
        gap: GRID_GAP,
        padding: '0 1.2vw 10px',
        borderBottom: '1px solid #eee',
        marginBottom: '10px',
      }}>
        {HEADERS.map((h, i) => (
          <span key={i} style={{ fontSize: '10px', color: '#999', fontWeight: 'bold', textTransform: 'uppercase' }}>
            {h}
          </span>
        ))}
        <DeleteCell><div style={{ width: '28px' }} /></DeleteCell>
      </div>

      {items.map((item) => (
        <MaterialItemContainer
          key={item.id}
          style={{ border: 'none', background: 'none', padding: '10px 0', margin: 0, gridTemplateColumns: '1fr' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: GRID_GAP,
            alignItems: 'center',
            padding: '0 1.2vw',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <input style={{ fontWeight: 'bold', border: 'none', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontSize: '11px' }} value={item.codigo || ''} readOnly />
              <input style={{ color: '#666', border: 'none', background: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontSize: '10px' }} value={item.descricao || ''} readOnly />
            </div>

            <div style={{ position: 'relative' }}>
              <input
                style={{ ...smallInputStyle, textAlign: 'left' }}
                value={activeNcmDropdown === item.id ? ncmSearch : (item.ncmCode || item.ncm || '')}
                placeholder={semEmissor ? 'Selecione a empresa emissora primeiro' : (item.cfopId && destStateId ? 'Buscar NCM' : 'Defina CFOP e destinatário')}
                disabled={isEmitida || semEmissor || !item.cfopId || !destStateId}
                onChange={(e) => { setNcmSearch(e.target.value); setActiveNcmDropdown(item.id); }}
                onFocus={() => { setNcmSearch(''); setActiveNcmDropdown(item.id); fetchNcmsForItem(item); }}
                onBlur={() => setTimeout(() => setActiveNcmDropdown(null), 200)}
                title="Selecionar NCM com cenário fiscal"
              />
              {activeNcmDropdown === item.id && filteredNcms.length > 0 && (
                <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px', minWidth: '220px' }}>
                  {filteredNcms.map((n) => (
                    <DropdownItem
                      key={n.id}
                      onMouseDown={() => {
                        // Mesmo produto (linhas de variantes do mesmo orderItemId) compartilha
                        // NCM/CFOP — a escolha numa linha propaga pras demais do grupo.
                        setItems((prev) =>
                          prev.map((p) =>
                            mesmoProduto(p, item)
                              ? { ...p, ncmId: n.id, ncmCode: n.ncm, ncm: n.ncm }
                              : p
                          )
                        );
                        setActiveNcmDropdown(null);
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#333' }}>{n.ncm}</span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </div>

            <div>
              <input style={pillInputStyle} value={item.cst || '—'} readOnly />
              {!item.cst && (
                <span style={{ fontSize: '9px', color: '#c53030', display: 'block', marginTop: '2px', lineHeight: 1.2 }}>
                  {item.cenarioAusente
                    ? 'sem cenário para este emissor'
                    : item.cenarioId
                    ? `cenário #${item.cenarioId} · natureza ${item.cenarioNatureza ?? '—'} sem CST de ICMS`
                    : ''}
                </span>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <input
                style={pillInputStyle}
                value={activeCfopDropdown === item.id ? cfopSearch : (item.cfop || '')}
                placeholder={semEmissor ? 'Selecione a empresa emissora primeiro' : 'Buscar CFOP'}
                disabled={isEmitida || semEmissor}
                onChange={(e) => { setCfopSearch(e.target.value); setActiveCfopDropdown(item.id); }}
                onFocus={() => { setCfopSearch(''); setActiveCfopDropdown(item.id); }}
                onBlur={() => setTimeout(() => setActiveCfopDropdown(null), 200)}
                title="Editar CFOP manualmente"
              />
              {!isEmitida && cenario?.personalizados.has(item.id) && (
                <PersonalizadoChip>
                  personalizado
                  <button
                    type="button"
                    aria-label="Voltar ao cenário fiscal dos itens"
                    title="Voltar ao cenário fiscal dos itens"
                    onClick={() => cenario.voltarAoTopo(items.filter((p) => mesmoProduto(p, item)).map((p) => p.id))}
                  >
                    ×
                  </button>
                </PersonalizadoChip>
              )}
              {!isEmitida && cenario?.semCenarioNoTopo(item.ncmId) && (
                <SemCenarioAviso>sem cenário em {cenario.nomeCenarioTopo}</SemCenarioAviso>
              )}
              {activeCfopDropdown === item.id && filteredCfops.length > 0 && (
                <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px', minWidth: '300px' }}>
                  {filteredCfops.map((cfop) => (
                    <DropdownItem
                      key={cfop.id}
                      onMouseDown={() => {
                        setItems((prev) =>
                          prev.map((p) =>
                            mesmoProduto(p, item)
                              ? { ...p, cfopId: cfop.id, cfop: cfop.code.toString() }
                              : p
                          )
                        );
                        cenario?.marcarTrocaManual(
                          items.filter((p) => mesmoProduto(p, item)).map((p) => p.id),
                          cfop.code.toString(),
                        );
                        setActiveCfopDropdown(null);
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#333' }}>{rotuloCfop(cfop)}</span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </div>

            <input style={{ ...smallInputStyle, textAlign: 'center' }} value={item.unidade || ''} readOnly />
            <input style={{ ...pillInputStyle, fontWeight: 'bold' }} value={item.quantidade || ''} readOnly />
            <input style={smallInputStyle} value={item.vlUnit || ''} readOnly />
            <input style={{ ...smallInputStyle, fontWeight: 'bold' }} value={item.vlTotal || ''} readOnly />
            <input style={smallInputStyle} value={item.bcIcms || ''} readOnly />
            <input style={smallInputStyle} value={item.vlIcms || ''} readOnly />
            <input
              style={smallInputStyle}
              value={item.vlIpi || ''}
              readOnly
              title={`CST ${item.cstIpi || 's/ CST'} · cEnq ${item.cEnqIpi || 's/ enquadramento'}`}
            />
            <input style={pillInputStyle} value={item.aliqIcms || '0'} readOnly />
            <IpiCell>
              <input
                style={pillInputStyle}
                value={item.aliqIpi || '0'}
                readOnly
                title={`CST ${item.cstIpi || 's/ CST'} · cEnq ${item.cEnqIpi || 's/ enquadramento'}`}
              />
              {item.origemIpi && (
                <OrigemIpiChip $semRegra={item.origemIpi === 'SEM_REGRA'}>
                  {ORIGEM_IPI_LABEL[item.origemIpi] ?? item.origemIpi}
                </OrigemIpiChip>
              )}
            </IpiCell>

            <DeleteCell>
            {!isEmitida && (
              <InlineDeleteButton
                onClick={() => onRemove({ id: Number(item.id), descricao: item.descricao, quantidade: Number(item.quantidade) })}
                style={{ marginTop: 0, background: '#feecec', borderRadius: '4px', width: '28px', height: '28px', minWidth: 'auto' }}
              >
                <img src={trash} alt="Excluir" style={{ width: '14px', filter: 'invert(37%) sepia(93%) saturate(3661%) hue-rotate(338deg) brightness(91%) contrast(107%)' }} />
              </InlineDeleteButton>
            )}
            {isEmitida && <div style={{ width: '28px' }} />}
            </DeleteCell>
          </div>

          <div style={{ display: 'flex', gap: '16px', padding: '6px 1.2vw 0', fontSize: '10px', color: '#888', flexWrap: 'wrap' }}>
            <span>CST PIS/COFINS: <strong>{item.cstPisCofins || '—'}</strong></span>
            <span>CST IBS/CBS: <strong>{item.cstIbsCbs || '—'}</strong></span>
            <span>Classif. trib.: <strong>{item.classificacao || '—'}</strong></span>
            <span>Vl. IBS: <strong>{item.vlIbs || '—'}</strong></span>
            <span>Vl. CBS: <strong>{item.vlCbs || '—'}</strong></span>
          </div>
        </MaterialItemContainer>
      ))}
      </ItensGrade>
      </ItensScroll>
    </StyledCard>
  );
};

export default ItensSection;
